import Icon from 'components/atoms/Icon';
import { useGetCompaniesOptions } from 'components/molecules/SelectFormField/CompanySelectFormFieldPaginated/CompanySelectFormFieldPaginated';
import { ItemsCount } from 'components/ui/ItemsCount/ItemsCount';
import { DEBOUNCE_TIME_DEFAULT } from 'constants/debounce';
import {
  SortOption,
  TechnicalSkillSortField,
  useTechnicalSkillFindManyPaginatedQuery,
} from 'generated/graphql';
import { useQueryParams } from 'hooks/useQueryParams';
import { debounce } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AddTechnicalSkillDrawer } from 'views/StaticContent/TechnicalSkills/AddTechnicalSkillDrawer/AddTechnicalSkillDrawer';
import { EditTechnicalSkillDrawer } from 'views/StaticContent/TechnicalSkills/EditTechnicalSkillDrawer/EditTechnicalSkillDrawer';
import { GridSortModel, Table } from '@spotted-zebra-uk/ui';
import {
  Button,
  Search,
  Select,
  Switch,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import TechnicalSkillsFilterModal from '../TechnicalSkillsFilterModal';
import {
  COLUMNS,
  DEFAULT_TABLE_PAGE_SIZE,
  getTableSkillSortField,
  Pagination,
  parseRow,
  SEMANTIC_SEARCH_THRESHOLD,
  TechnicalSkillFilters,
  TechnicalSkillTableColumns,
} from './TechnicalSkillsList.helpers';
import styles from './TechnicalSkillsList.module.scss';

const TechnicalSkillsList = () => {
  const rowCountRef = useRef(0);
  const { handleMsgType } = useNotification();

  // Parsing URL query params data
  const [params, setParams] = useQueryParams([
    'searchValue',
    'orderDirection',
    'orderField',
    'skillCategoryNamesFilter',
    'page',
    'perPage',
    'withDeleted',
    'ownerFilter',
    'editTechnicalSkill',
    'addTechnicalSkill',
  ]);

  const searchValue = params.searchValue || '';
  const orderDirection =
    params.orderDirection === SortOption.Desc
      ? SortOption.Desc
      : SortOption.Asc;
  const orderField =
    (params.orderField as TechnicalSkillSortField) ||
    TechnicalSkillSortField.Name;
  const skillCategoryNamesFilter =
    params.skillCategoryNamesFilter?.split(',').filter(category => category) ||
    [];
  const currentTablePage = params.page ? Number(params.page) : 1;
  const currentTablePageSize = params.perPage
    ? Number(params.perPage)
    : DEFAULT_TABLE_PAGE_SIZE;
  const withDeleted = Boolean(params.withDeleted);
  const ownerFilter = params.ownerFilter
    ? Number(params.ownerFilter)
    : undefined;

  // API Query
  const { data, loading } = useTechnicalSkillFindManyPaginatedQuery({
    variables: {
      semanticSearch: searchValue
        ? {
            value: searchValue,
            threshold: SEMANTIC_SEARCH_THRESHOLD,
          }
        : null,
      pagination: {
        page: currentTablePage,
        size: currentTablePageSize,
      },
      order: {
        field: orderField,
        direction: orderDirection,
      },
      filters: {
        skillCategoryNames: skillCategoryNamesFilter,
        withDeleted,
        companyId: ownerFilter,
      },
    },
    fetchPolicy: 'cache-and-network',
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message:
          error.message || 'Error occurred while getting technical skills',
      });
    },
  });

  // Search
  const handleSearch = (value: string) => {
    setParams({ searchValue: value, page: '1' });
  };

  const debouncedSetSearchValue = debounce(handleSearch, DEBOUNCE_TIME_DEFAULT);

  const handleSearchClearBtnClick = () => {
    setParams({ searchValue: null, page: '1' });
  };

  // Pagination
  const handlePaginationChange = ({
    page: newPage,
    pageSize: newPageSize,
  }: Pagination) => {
    const parsedNewPage = newPage + 1;

    if (
      currentTablePage !== parsedNewPage ||
      currentTablePageSize !== newPageSize
    ) {
      setParams({
        page: parsedNewPage.toString(),
        perPage: newPageSize.toString(),
      });
    }
  };

  // Table data
  const rows = useMemo(() => {
    return data?.TechnicalSkillFindManyPaginated.data?.map(technicalSkill =>
      parseRow(technicalSkill, () =>
        setParams({ editTechnicalSkill: technicalSkill.subId })
      )
    );
  }, [data?.TechnicalSkillFindManyPaginated.data, setParams]);

  const rowCount = useMemo(() => {
    const pageInfo = data?.TechnicalSkillFindManyPaginated.pageInfo;
    if (pageInfo?.itemsTotal !== undefined) {
      rowCountRef.current = pageInfo.itemsTotal;
    }
    return rowCountRef.current;
  }, [data?.TechnicalSkillFindManyPaginated.pageInfo]);

  // With Deleted filter
  const setWithDeleted = () => {
    setParams({ withDeleted: withDeleted ? null : 'true' });
  };

  // Owner company field
  const setOwnerFilter = ({ value }: TSelectOption<number | null>) => {
    setParams({ ownerFilter: value ? value.toString() : null });
  };

  // Ordering
  const handleSortChange = useCallback(
    (sortModel: GridSortModel) => {
      if (!sortModel[0]) return;

      const { field, sort } = sortModel[0];
      const newOrderField = getTableSkillSortField(
        field as TechnicalSkillTableColumns
      );

      if (!newOrderField) {
        return setParams({ orderDirection: null, orderField: null });
      }

      if (sort === 'asc') {
        return setParams({
          orderDirection: SortOption.Asc,
          orderField: newOrderField,
        });
      }

      return setParams({
        orderDirection: SortOption.Desc,
        orderField: newOrderField,
      });
    },
    [setParams]
  );

  // Filters
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const handleSubmitFilters = ({
    skillCategoryNames,
  }: TechnicalSkillFilters) => {
    setParams({
      skillCategoryNamesFilter: skillCategoryNames.length
        ? skillCategoryNames.join(',')
        : null,
    });
  };

  return (
    <div className={styles.container} data-testid="technical-skills-list">
      <Table
        columns={COLUMNS}
        rows={rows}
        autosizeOnMount
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={{
          page: currentTablePage - 1,
          pageSize: currentTablePageSize,
        }}
        onPaginationModelChange={handlePaginationChange}
        keepNonExistentRowsSelected
        disableColumnFilter
        hasToolbar
        onSortModelChange={handleSortChange}
        slotProps={{
          toolbar: {
            leftMainAction: (
              <ItemsCount
                count={rowCount}
                ariaLabelSuffix="technical skills in the table"
              />
            ),
            left: (
              <CompanyOwnerSelect
                onChange={setOwnerFilter}
                companyId={ownerFilter}
              />
            ),
            right: (
              <TableFilters
                onWithDeletedToggle={setWithDeleted}
                withDeletedValue={withDeleted}
                onSearch={debouncedSetSearchValue}
                searchValue={searchValue}
                openFiltersModal={() => setIsFiltersModalOpen(true)}
                onSearchClearBtnClick={handleSearchClearBtnClick}
                filtersCounter={skillCategoryNamesFilter.length ? 1 : undefined}
              />
            ),
            rightMainAction: (
              <Button
                onClick={() => setParams({ addTechnicalSkill: 'true' })}
                variant="primary"
                size="medium"
                leftIcon={<Icon icon="add" className={styles.plusIcon} />}
              >
                Add skill
              </Button>
            ),
          },
        }}
        sx={{
          minHeight: '600px',
        }}
      />
      <TechnicalSkillsFilterModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        onSubmit={handleSubmitFilters}
        filters={{ skillCategoryNames: skillCategoryNamesFilter }}
      />
      <AddTechnicalSkillDrawer
        open={Boolean(params.addTechnicalSkill)}
        onClose={() => setParams({ addTechnicalSkill: null })}
      />
      <EditTechnicalSkillDrawer
        open={Boolean(params.editTechnicalSkill)}
        onClose={() => setParams({ editTechnicalSkill: null })}
        technicalSkillSubId={params.editTechnicalSkill}
      />
    </div>
  );
};

type CompanyOwnerSelectProps = {
  onChange: ({ value }: TSelectOption<number | null>) => void;
  companyId?: number;
};

function CompanyOwnerSelect({ onChange, companyId }: CompanyOwnerSelectProps) {
  const companyOptionsHelpers = useGetCompaniesOptions({
    hasGlobal: true,
    initialValue: companyId,
  });

  const selectedCompanyOption =
    companyId !== undefined
      ? companyOptionsHelpers.options.find(option => option.value === companyId)
      : {
          label: '',
          value: undefined,
        };

  return (
    <div className={styles.toolbarLeft}>
      <Select
        id="company-selector"
        options={companyOptionsHelpers.options}
        onChange={onChange}
        name="Owner"
        className={styles.toolbarCompanySelector}
        isLoading={companyOptionsHelpers.loading}
        onClearBtnClick={() => onChange({ value: null })}
        placeholder="Select owner"
        onMenuScrollToBottom={companyOptionsHelpers.loadMore}
        loadingPaginatedOptions={companyOptionsHelpers.paginationLoading}
        onInputChange={companyOptionsHelpers.debouncedSearch}
        disableFilter
        value={selectedCompanyOption}
      />
    </div>
  );
}

type TableFiltersProps = {
  onWithDeletedToggle: () => void;
  withDeletedValue: boolean;
  onSearch: (text: string) => void;
  searchValue: string;
  openFiltersModal: () => void;
  filtersCounter?: number;
  onSearchClearBtnClick: () => void;
};

function TableFilters({
  onWithDeletedToggle,
  withDeletedValue,
  onSearch,
  searchValue,
  openFiltersModal,
  filtersCounter,
  onSearchClearBtnClick,
}: TableFiltersProps) {
  return (
    <div className={styles.toolbarRight}>
      <Switch
        id="display-archived-toggle"
        label="Display archived"
        onToggle={onWithDeletedToggle}
        value={withDeletedValue}
        size="large"
      />
      <form onSubmit={e => e.preventDefault()} className={styles.searchForm}>
        <Search
          internalSearch={false}
          onSearch={onSearch}
          inputValue={searchValue}
          onClearBtnClick={onSearchClearBtnClick}
          ariaLabel="Search"
          className={styles.toolbarSearch}
          ariaDisabled={false}
          value={{
            value: '',
            label: searchValue,
          }}
        />
      </form>
      <Button
        variant="secondary"
        leftIcon={<Icon icon="filter" />}
        onClick={openFiltersModal}
        data-testid="technical-skills-list__filter-button"
        counter={filtersCounter}
        size="small"
      >
        Filter
      </Button>
    </div>
  );
}

export default TechnicalSkillsList;
